import React, { useState, useEffect } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/accountSection/PersonalData";
import UploadAvatar from "../../../../components/uploadAvatar";
import { userService } from "../../../../services/user.service";
import { useCombinedStore } from "../../../../store";

interface PersonalDataProps {
  isEditing: boolean;
  formData: any;
  decryptedToken: any;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => void;
  handleSave: (objectType: any) => void;
  updateState: (newState: any) => void;
  uploadedImage: string;
}

const PersonalData: React.FC<PersonalDataProps> = ({
  isEditing,
  formData,
  decryptedToken,
  handleInputChange,
  handleSave,
  updateState,
  uploadedImage,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [tempImage, setTempImage] = useState<string>("");
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [displayImage, setDisplayImage] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const { authUserZustand, setAuthUserZustand } = useCombinedStore((state) => {
    return {
      authUserZustand: state.authUserZustand,
      setAuthUserZustand: state.setAuthUserZustand,
    };
  });

  const displayData = isEditing
    ? formData
    : decryptedToken?.userInfo || formData;

  useEffect(() => {
    const currentImage = decryptedToken?.userInfo?.userLogoUrl || "";
    if (currentImage && currentImage.startsWith("http")) {
      setDisplayImage(currentImage);
    } else if (currentImage && currentImage.startsWith("data:")) {
      setDisplayImage(currentImage);
    } else if (currentImage) {
      setDisplayImage(`data:image/jpeg;base64,${currentImage}`);
    } else {
      setDisplayImage("");
    }
  }, [decryptedToken]);

  // Validação em tempo real
  const validateField = (field: string, value: string): boolean => {
    if (field === "userName" || field === "userSurname") {
      return value.trim().length > 0;
    }
    return true;
  };

  const handleInputChangeWithValidation = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    const value = e.target.value;
    handleInputChange(e, field);

    // Validação em tempo real
    if (isEditing && (field === "userName" || field === "userSurname")) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: !validateField(field, value),
      }));
    }
  };

  const handleImageUpload = async (image: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);

      setTempImageFile(image);

      // Cria preview da imagem
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string;
        setTempImage(base64Image);
        setDisplayImage(base64Image);
      };
      reader.readAsDataURL(image);

      // Simula progresso
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      if (!isEditing) {
        await saveProfilePicture(image);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        setUploadProgress(0);
        setIsUploading(false);
      }, 500);
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      setIsUploading(false);
      setUploadProgress(0);
      updateState({
        message: "Erro ao processar imagem. Tente novamente.",
        messageType: "error",
      });
    }
  };

  const saveProfilePicture = async (image: File) => {
    try {
      setIsSaving(true);

      const userUpdatedData = {
        ...decryptedToken?.userInfo,
      };

      const response = await userService.updateProfilePicture(
        image,
        userUpdatedData
      );
      if (!response) {
        throw new Error(
          "Token não recebido após atualização da foto de perfil"
        );
      }
      setAuthUserZustand({
        authUser: {
          ...authUserZustand,
          Token: response,
        },
      });

      updateState({
        message: "Foto de perfil atualizada com sucesso!",
        messageType: "success",
      });

      setTempImage("");
      setTempImageFile(null);
    } catch (error: any) {
      console.error("Erro ao atualizar foto de perfil:", error);
      updateState({
        message:
          error.response?.data?.message ||
          error.message ||
          "Erro ao atualizar foto de perfil. Tente novamente.",
        messageType: "error",
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePersonalData = async () => {
    // Validação antes de salvar
    const errors: Record<string, boolean> = {};
    if (!formData.userName || formData.userName.trim() === "") {
      errors.userName = true;
    }
    if (!formData.userSurname || formData.userSurname.trim() === "") {
      errors.userSurname = true;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      updateState({
        message: "Por favor, preencha todos os campos obrigatórios.",
        messageType: "error",
      });
      return;
    }

    try {
      setIsSaving(true);

      const updateData: any = { ...decryptedToken?.userInfo };
      if (
        formData.userName &&
        formData.userName !== decryptedToken?.userInfo?.userName
      ) {
        updateData.userName = formData.userName;
      }
      if (
        formData.userSurname &&
        formData.userSurname !== decryptedToken?.userInfo?.userSurname
      ) {
        updateData.userSurname = formData.userSurname;
      }
      if (
        formData.userSocialName !== decryptedToken?.userInfo?.userSocialName
      ) {
        updateData.userSocialName = formData.userSocialName || null;
      }

      if (Object.keys(updateData).length > 0) {
        const response = await userService.updateUser(updateData);

        if (!response) {
          throw new Error(
            "Token não recebido após atualização da foto de perfil"
          );
        }

        setAuthUserZustand({
          authUser: {
            ...authUserZustand,
            Token: response,
          },
        });
      }

      handleSave("personalData");
      setFieldErrors({});
      updateState({
        message: "Dados atualizados com sucesso!",
        messageType: "success",
        isEditing: false,
      });
    } catch (error: any) {
      console.error("Erro ao salvar dados:", error);
      updateState({
        message:
          error.response?.data?.message ||
          "Erro ao salvar dados. Tente novamente.",
        messageType: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFieldErrors({});
    updateState({
      isEditing: false,
    });
  };

  return (
    <AlfredoStyles.FieldsContainer>
      <AlfredoStyles.AvatarSection>
        <AlfredoStyles.AvatarWrapper $isUploading={isUploading || isSaving}>
          <UploadAvatar
            onImageConfirmed={handleImageUpload}
            imageType={1}
            initialImage={displayImage}
          />
          {(isSaving || isUploading) && (
            <AlfredoStyles.LoadingOverlay>
              <AlfredoStyles.LoadingContent>
                {isUploading ? (
                  <>
                    <AlfredoStyles.LoadingSpinner />
                    <div>Enviando: {uploadProgress}%</div>
                    <AlfredoStyles.LoadingSubtext>
                      Fazendo upload para nuvem...
                    </AlfredoStyles.LoadingSubtext>
                  </>
                ) : (
                  <>
                    <AlfredoStyles.LoadingSpinner />
                    <div>Salvando...</div>
                  </>
                )}
              </AlfredoStyles.LoadingContent>
            </AlfredoStyles.LoadingOverlay>
          )}
        </AlfredoStyles.AvatarWrapper>
        {!isEditing && (
          <AlfredoStyles.AvatarHint>
            Clique na imagem para alterar
          </AlfredoStyles.AvatarHint>
        )}
      </AlfredoStyles.AvatarSection>

      <AlfredoStyles.FormGrid>
        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label $required>
            <AlfredoStyles.MaterialIcons>person</AlfredoStyles.MaterialIcons>
            Nome
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="text"
            value={displayData.userName || ""}
            onChange={(e) => handleInputChangeWithValidation(e, "userName")}
            disabled={!isEditing}
            placeholder="Seu nome"
            $hasError={fieldErrors.userName}
          />
          {fieldErrors.userName && (
            <AlfredoStyles.ErrorText>
              Nome é obrigatório
            </AlfredoStyles.ErrorText>
          )}
        </AlfredoStyles.InputWrapper>

        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label $required>
            <AlfredoStyles.MaterialIcons>badge</AlfredoStyles.MaterialIcons>
            Sobrenome
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="text"
            value={displayData.userSurname || ""}
            onChange={(e) => handleInputChangeWithValidation(e, "userSurname")}
            disabled={!isEditing}
            placeholder="Seu sobrenome"
            $hasError={fieldErrors.userSurname}
          />
          {fieldErrors.userSurname && (
            <AlfredoStyles.ErrorText>
              Sobrenome é obrigatório
            </AlfredoStyles.ErrorText>
          )}
        </AlfredoStyles.InputWrapper>
      </AlfredoStyles.FormGrid>

      <AlfredoStyles.FormGrid>
        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label>
            <AlfredoStyles.MaterialIcons>
              how_to_reg
            </AlfredoStyles.MaterialIcons>
            Nome Social
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="text"
            value={displayData.userSocialName || ""}
            onChange={(e) => handleInputChange(e, "userSocialName")}
            disabled={!isEditing}
            placeholder="Seu nome social (opcional)"
          />
        </AlfredoStyles.InputWrapper>

        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label>
            <AlfredoStyles.MaterialIcons>email</AlfredoStyles.MaterialIcons>
            Email
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="email"
            value={displayData.userEmail || ""}
            onChange={(e) => handleInputChange(e, "userEmail")}
            disabled={true}
            placeholder="seu@email.com"
            $isReadOnly
          />
          <AlfredoStyles.FieldHint>
            Email não pode ser alterado
          </AlfredoStyles.FieldHint>
        </AlfredoStyles.InputWrapper>
      </AlfredoStyles.FormGrid>

      <AlfredoStyles.FormGrid>
        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label>
            <AlfredoStyles.MaterialIcons>phone</AlfredoStyles.MaterialIcons>
            Telefone
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="tel"
            value={displayData.userPhoneNumber || ""}
            onChange={(e) => handleInputChange(e, "userPhoneNumber")}
            disabled={!isEditing}
            placeholder="(11) 99999-9999"
          />
        </AlfredoStyles.InputWrapper>

        <AlfredoStyles.InputWrapper>
          <AlfredoStyles.Label>
            <AlfredoStyles.MaterialIcons>group</AlfredoStyles.MaterialIcons>
            Grupo de Usuário
          </AlfredoStyles.Label>
          <AlfredoStyles.Input
            type="text"
            value={displayData.userGroupName || ""}
            disabled={true}
            placeholder="Seu grupo de usuário"
            $isReadOnly
          />
          <AlfredoStyles.FieldHint>
            Grupo gerenciado pelo administrador
          </AlfredoStyles.FieldHint>
        </AlfredoStyles.InputWrapper>
      </AlfredoStyles.FormGrid>

      {(tempImage || tempImageFile) && !isSaving && !isUploading && (
        <AlfredoStyles.SuccessBanner>
          <AlfredoStyles.MaterialIcons>
            check_circle
          </AlfredoStyles.MaterialIcons>
          <div>
            <AlfredoStyles.SuccessBannerTitle>
              Nova imagem selecionada
            </AlfredoStyles.SuccessBannerTitle>
            <AlfredoStyles.SuccessBannerText>
              {!isEditing
                ? "Imagem salva automaticamente no armazenamento em nuvem."
                : "Clique em 'Salvar Alterações' para confirmar."}
            </AlfredoStyles.SuccessBannerText>
          </div>
        </AlfredoStyles.SuccessBanner>
      )}

      <AlfredoStyles.ButtonWrapper>
        {isEditing ? (
          <>
            <AlfredoStyles.Button
              onClick={handleSavePersonalData}
              disabled={isSaving || isUploading}
            >
              {isSaving ? (
                <>
                  <AlfredoStyles.ButtonSpinner />
                  Salvando...
                </>
              ) : (
                <>
                  <AlfredoStyles.MaterialIcons>
                    save
                  </AlfredoStyles.MaterialIcons>
                  Salvar Alterações
                </>
              )}
            </AlfredoStyles.Button>
            <AlfredoStyles.SecondaryButton
              onClick={handleCancel}
              disabled={isSaving || isUploading}
            >
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
          </>
        ) : (
          <AlfredoStyles.Button
            onClick={() => updateState({ isEditing: true })}
            disabled={isUploading}
          >
            <AlfredoStyles.MaterialIcons>edit</AlfredoStyles.MaterialIcons>
            Editar Informações
          </AlfredoStyles.Button>
        )}
      </AlfredoStyles.ButtonWrapper>
    </AlfredoStyles.FieldsContainer>
  );
};

export default PersonalData;
